import type { CustomPluginArgs, CustomValues } from '@atlassian/browser-metrics/types';

import { getAssetCacheStatus } from '@confluence/cached-assets/entry-points/getAssetCacheStatus.ts';
import { expVal } from '@confluence/feature-experiments';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import { collectDurationFromPerfMarkers } from '../collectFromPerfMarkers.ts';
import {
	browserMetricsState,
	getHTTPProtocol,
	transformObjectToFlattenCustomValuesType,
	convertHydrationErrors,
	getHydrationErrorCounts,
	convertWebPanelChanges,
	getSmartLinksStats,
	// eslint-disable-next-line import/no-cycle
} from '../utils/index.ts';
import { getNonContentAppKeys } from '../NonContentAppKeys.ts';
import { getAppKeys } from '../AppKeys.ts';

import { getCacheAttributes } from './getCacheAttributes.ts';

declare global {
	interface Window {
		__HYDRATABLE__?: boolean;
		__HYDRATION_FAILED__?: boolean;
		__APP_KEYS__?: string[];
	}
}

type SSRDataFetcherStats = {
	boundaries: string[];
	fetchCounts: Record<string, number>;
};

function limitTop10(obj: Record<string, any>): Record<string, any> {
	return Object.fromEntries(
		Object.entries(obj)
			.sort((a, b) => b[1] - a[1])
			.slice(0, 10),
	);
}

const sanitizeSsrMeasureName = (value: string): string => value.replace(/[^\-.\w]/g, '_');

const emptySSRDataFetcherStats = (): SSRDataFetcherStats => ({
	boundaries: [],
	fetchCounts: {},
});

const getSSRDataFetcherStats = (
	ssrMeasures: Record<string, unknown> | undefined,
): SSRDataFetcherStats => {
	if (!ssrMeasures) {
		return emptySSRDataFetcherStats();
	}

	const stats = emptySSRDataFetcherStats();
	const boundaryNames = new Set<string>();

	Object.keys(ssrMeasures).forEach((measureKey) => {
		const dataFetcherMatch = measureKey.match(/(?:^|\/)dataFetcher:([^/]+)/);
		if (!dataFetcherMatch) {
			return;
		}

		const boundaryName = sanitizeSsrMeasureName(dataFetcherMatch[1]);
		const measureLeaf = measureKey.substring(measureKey.lastIndexOf('/') + 1);

		if (measureLeaf?.startsWith('fetch:')) {
			boundaryNames.add(boundaryName);
			stats.fetchCounts[boundaryName] = (stats.fetchCounts[boundaryName] ?? 0) + 1;
		}
	});

	stats.boundaries = Array.from(boundaryNames).sort();

	return stats;
};

const getSSRBoundaryFirstApiMetrics = (
	ssrDataFetcherStats: SSRDataFetcherStats,
	navigationType: 'initial' | 'transition',
): CustomValues =>
	transformObjectToFlattenCustomValuesType({
		apiByStreamingBoundary: Object.fromEntries(
			ssrDataFetcherStats.boundaries.map((boundaryName) => [
				boundaryName,
				{
					ssr: {
						[navigationType]: {
							count: ssrDataFetcherStats.fetchCounts[boundaryName] ?? 0,
						},
					},
				},
			]),
		),
	});

// pageLoadEventPlugin provides customized Confluence specific attributes that will be added to all PAGE_LOAD events.
export const pageLoadEventPlugin = ({ stop }: CustomPluginArgs): CustomValues => {
	const isInitialPageLoad = browserMetricsState.transitionId === 0;
	const isSsrRendered = isInitialPageLoad && Boolean(window.__SSR_RENDERED__);
	const customAttributes: CustomValues = {
		transitionId: browserMetricsState.transitionId,
		// HTTP protocol used to fetch resources
		// Use multiple domains for http/1.1 users
		// Set in packages/confluence-frontend-server/templates/generator.js
		domainSharding: Boolean(window.__GET_CC_ASSET_URL),
		// HTTP protocol used to fetch resources
		httpProtocol: getHTTPProtocol(performance.getEntriesByType('resource')),
		// Add the critical path JS assets size stats
		criticalAssets: getCacheAttributes(stop),
		// Send stats on expensive queries and unnecessary watches
		apolloDiffStoreStats: transformObjectToFlattenCustomValuesType(
			limitTop10(window?.__APOLLO_STATS__?.diffStore || {}),
		),
		// Content + non-content 3P app keys for this navigation.
		//
		// Emitted for BOTH initial page loads and SPA transitions, unlike the
		// legacy `connectAppKeys` below which is initial-load only. Experiences
		// that are almost always reached via a transition (`edit-page`,
		// `live-edit`, `confluence-home`) therefore only ever report app keys
		// through this attribute.
		//
		// `connectAppKeys` is intentionally left as-is so existing dashboards
		// keep their current semantics; on an initial page load the two
		// attributes hold identical values.
		...(expVal('confluence_app_keys_transition', 'isEnabled', false)
			? { appKeys: getAppKeys() }
			: {}),
	};

	const navigationType = isInitialPageLoad ? 'initial' : 'transition';
	const ssrDataFetcherStats = isInitialPageLoad
		? getSSRDataFetcherStats((window as any).__SSR_MEASURES__)
		: emptySSRDataFetcherStats();

	Object.assign(customAttributes, {
		navigationType,
		renderMode: isSsrRendered ? 'ssr' : 'csr',
		apiSsrCount: isInitialPageLoad ? ((window as any).__SSR_OPERATIONS_COUNT__ ?? 0) : 0,
		...getSSRBoundaryFirstApiMetrics(ssrDataFetcherStats, navigationType),
	});

	Object.entries(collectDurationFromPerfMarkers('CFP-63.')).forEach(([fieldName, value]) => {
		customAttributes[`cfp63Durations:${fieldName}`] = value as number;
	});

	if (isInitialPageLoad) {
		const navigationTimings = window.performance.getEntriesByType('navigation');

		if (navigationTimings.length) {
			// TypeScript isn't returning the proper type for this, so some
			// casting here
			const firstTiming = navigationTimings[0] as PerformanceNavigationTiming;

			if (firstTiming.transferSize != null) {
				customAttributes.pageLoadedFromCache = firstTiming.transferSize === 0;
			}
		}

		// Record the current version of jQuery
		customAttributes.jqVer = (window as any).jQuery?.fn?.jquery;

		// Record if page is rendered by React SSR service
		customAttributes.ssr = isSsrRendered;
		// Some SSR render errors (e.g.: edit page content area failed to SSR) are considered critical even when window.__SSR_RENDERED__ is true
		customAttributes.ssrHasCriticalError = Boolean(window.__SSR_HAS_CRITICAL_ERROR__);
		customAttributes.ssrTraceId = window.__SSR_TRACE_ID__ ?? '';

		// only send ssr macro metrics if there is at least one macro on the SSRd page
		if (window?.__SSR_MACROS_INFO__) {
			customAttributes.ssrMacrosInfo = window.__SSR_MACROS_INFO__;
		}

		if (window?.__SSR_MACRO_PRELOAD_TOPX_FAILS__) {
			customAttributes.macrosPreloadFails = window?.__SSR_MACRO_PRELOAD_TOPX_FAILS__;
		}

		// Record user clicks and intent to click on SSR placeholder elements
		customAttributes.ssrInteractions = window?.__SSR_INTERACTIONS__ || {};
		// @ts-ignore `__SSR_INTERACTIONS_DURATIONS__` maintains an array of numbers, but browser-metrics doesn't really accept arrays. Might make sense to comma-join the values
		customAttributes.ssrInteractionsDurations = window?.__SSR_INTERACTIONS_DURATIONS__ || {};
		customAttributes.ssrQueryTimeouts = window?.__SSR_QUERY_TIMEOUTS__ || {};
		customAttributes.ssrPartialSuccess = window?.__SSR_PARTIAL_SUCCESS__ || {};

		if (window?.__SSR_STREAM_END__) {
			customAttributes.ssrStreamEnd = window.__SSR_STREAM_END__;
		}
		customAttributes.reactStreaming = window.__SSR_REACT_STREAMING__ || false;

		// Record loadable ids that timed out
		customAttributes.loadableTimeouts = Array.from(
			new Set(window?.__LOADABLE_TIMEOUTS__ || []),
		).join(',');

		if (window?.__HYDRATION_ERRORS__) {
			customAttributes.hydrationErrors = convertHydrationErrors(window.__HYDRATION_ERRORS__);
			if (StatsigConfigurations.getBoolean('confluence_hydration_error_type_logging', false)) {
				customAttributes.hydrationErrorCounts = getHydrationErrorCounts(
					window.__HYDRATION_ERRORS__,
				);
			}

			if (window.__HYDRATION_ERRORS__.length > 0) {
				getMonitoringClient().incrementCounter('hydrationErrorSessions');
			}
		}

		customAttributes.hydratable = window?.__HYDRATABLE__ || false;
		customAttributes.hydrationFailed = window?.__HYDRATION_FAILED__ || false;

		// Record the number of SSRed elements
		customAttributes.totalSSRedElements = window?.__TOTAL_SSR_ELEMENTS__ || 0;

		if (window?.__SSR_SMARTLINKS_STATS__) {
			customAttributes.ssrSmartLinksStats = getSmartLinksStats(window.__SSR_SMARTLINKS_STATS__);
		}

		if (window?.__SSR_SHORTCUTS_STATS__) {
			customAttributes.ssrShortcutsStats = window.__SSR_SHORTCUTS_STATS__;
		}

		// Calculates which query is the long pole, as well as its duration
		customAttributes.longPoleQuery = window?.__SSR_LONG_POLE_QUERY__ || {};

		// Node counts from AK renderer, available in fabric only
		customAttributes.rendererNodesCount = window?.__RENDERER_NODES_COUNT__ || {};
		const initialLightNodeMetrics = window.__LIGHT_RENDERER_INITIAL_NODE_METRICS__;
		if (initialLightNodeMetrics) {
			const remaining = initialLightNodeMetrics.getRemainingLightNodes?.(stop);
			if (remaining) {
				customAttributes.lightRemainingCount = remaining.count;
				customAttributes.lightRemainingPct = remaining.percent;
				customAttributes.lightOffscreenUpgradeCount = remaining.offscreenUpgraded;
			}
			customAttributes.lightInitialCount = initialLightNodeMetrics.count;
			customAttributes.lightInitialPct = initialLightNodeMetrics.percent;
			if (initialLightNodeMetrics.fullNodeCountsByReason) {
				Object.assign(
					customAttributes,
					transformObjectToFlattenCustomValuesType({
						fullInitialCount: initialLightNodeMetrics.fullNodeCountsByReason,
					}),
				);
			}
			if (initialLightNodeMetrics.fullBatchTargetPx !== undefined) {
				customAttributes.fullBatchTargetPx = initialLightNodeMetrics.fullBatchTargetPx;
			}
			if (initialLightNodeMetrics.fullBatchTargetViewportRatio !== undefined) {
				customAttributes.fullBatchViewportRatio =
					initialLightNodeMetrics.fullBatchTargetViewportRatio;
			}
		}
		customAttributes.remoteLocalStorage = window?.__REMOTE_LOCAL_STORAGE__?.length || 0;
		customAttributes.wrmKeys = window?.__WRM_KEYS__ || {};
		// Combine content connect app keys (from LegacyMacroRendererComponent) with
		// non-content 3P app keys (byline/page banner/context menu/web panels/etc.)
		// so both land in the same `connectAppKeys` extra_property. Deduped via a Set.
		customAttributes.connectAppKeys = [
			...new Set([...(window?.__APP_KEYS__ || []), ...getNonContentAppKeys()]),
		].join(',');

		customAttributes.atlGeneralHeightChanges =
			transformObjectToFlattenCustomValuesType(convertWebPanelChanges());

		// Add the caching status of each asset
		const cacheStatus = transformObjectToFlattenCustomValuesType({
			cacheStatus: getAssetCacheStatus(stop),
		});
		for (const [key, value] of Object.entries(cacheStatus)) {
			customAttributes[key] = value;
		}
	}

	return customAttributes;
};
